"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.Aggregations = void 0;
const abstract_query_1 = require("../abstract/abstract-query");
/**
 * ### Overview
 * From the official documentation of the OpenSearch project.
 * @see https://opensearch.org/docs/latest/aggregations/
 *
 * Aggregations let you tap into OpenSearch’s powerful analytics engine to analyze your data and extract statistics from it.
 *
 * The use cases of aggregations vary from analyzing data in real time to take some action to using OpenSearch Dashboards to create a visualization dashboard.
 *
 * OpenSearch can perform aggregations on massive datasets in milliseconds. Compared to queries, aggregations consume more CPU cycles and memory.
 *
 * @description
 * This query builder covers three categories:
 * @see {@link MetricAggregationsSchema}
 * - Metric aggregations that calculate metrics, such as a sum or average, from field values;
 * @see {@link BucketAggregationsSchema}
 * - Bucket aggregations that group documents into buckets, also called bins, based on field values, ranges, or other criteria;
 * @see {@link PipelineAggregationsSchema}
 * - Pipeline aggregations that compute complex statistical and mathematical measures like derivatives, moving averages, cumulative sums, and so on.
 *
 * ### Usage
 * ```typescript
 * const aggregations = new Aggregations<BucketAggregationsSchema>();
 * aggregations.addProp('histogram', { field: 'price', interval: 50 });
 * aggregations.generate();
 * ```
 *
 * or
 * ```typescript
 * const aggregations = new Aggregations<BucketAggregationsSchema>()
 *  .addProp('histogram', { field: 'price', interval: 50 })
 *  .generate();
 * ```
 *
 * ### Overriding / Overloading
 * If you wish to override/overload this class, you may do so by extending this class, and providing either {@link AggregationsSchema}, {@link MetricAggregationsSchema}, {@link BucketAggregationsSchema} as the `SCHEMA`.
 *
 * For instance,
 * ```typescript
 * export class AggregationsOverride extends Aggregations<MetricAggregationsSchema> {
 *  constructor() {
 *   super();
 *  }
 *
 *  addPropsOverride(..params: any) {
 *    // DO SOMETHING
 *    this.addProps(...params);
 *  }
 * }
 * ```
 * To implement it,
 * ```typescript
 * const aggregations = new AggregationsOverride<MetricAggregationsSchema>();
 * aggregations.addPropsOverride(...args);
 * aggregations.generate();
 * ```
 * or
 *
 * ```typescript
 * const aggregations = new AggregationsOverride<MetricAggregationsSchema>()
 *  .addPropsOverride(...args)
 *  .generate();
 * ```
 */
class Aggregations extends abstract_query_1.AbstractQuery {
    /**
     * Adds and defines the property of the raw query and the associated field (optional) to search.
     *
     * @param prop A property based on the provided SCHEMA
     * @param data A property-specific data definition that will define the query
     * @param config
     *   @param field A field to search
     *   @param identifier A name or identifier of the query
     * @returns Query
     */
    addQuery(prop, data, config) {
        return this.updateQuery(prop, data, config);
    }
}
exports.Aggregations = Aggregations;
